import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {Session} from "../../../services/Session";
import {TAX_PLANNING_TYPES} from "../../../constants/tax-planning.constants";
import {LoadingService} from "../../../services/LoadingService";
import {ToastService} from "../../../services/Toast.service";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {ManageTaxStrategiesServices} from "../../../tpiq-services/manage-tax-strategies.service";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {isValidEmail} from "../../../utils/validators";

declare const _: any;

@Component({
  selector: 'app-create-or-update-client',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent],
  templateUrl: './create-or-update-client.component.html',
  styleUrl: './create-or-update-client.component.scss'
})
export class CreateOrUpdateClientComponent implements OnInit, OnDestroy {
  @Input() editMode: any;
  @Input() clientId: any;
  @Output() onCancelCreateOrUpdateOfClient: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCreationOrUpdateOfClient: EventEmitter<any> = new EventEmitter<any>();
  org: string = Session.getOrg();
  user: any = {};
  client: any = {};
  tempLabelText: string;
  types: Array<any> = TAX_PLANNING_TYPES;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  statusTypes: Array<any> = [{name: 'Potential Client'}, {name: 'Client'}, {name: 'Former Client'}, {name: 'Closed - Lost'}, {name: 'Custom'}];
  currentPlan: string;
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  portalMessage = 'NOTE: Please upgrade by emailing support@taxplaniq.com';

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private taxStrategyServices: ManageTaxStrategiesServices
  ) {}

  ngOnInit(): void {
    this.user = Session.getUser();
    this.getSubscriptionInfo();
    if (this.editMode) {
      this.getClientData();
    } else {
      this.client.status = 'Potential Client';
      this.client.customStatus = '';
      this.addDefaultLines();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  getClientData() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClient(this.org, this.user.id, this.clientId)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.client = _.cloneDeep(response) || {
          relatedParties: [],
          thirdParties: []
        };
        this.addDefaultLines();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get client data');
      });
  }

  addDefaultLines(){
    if ((this.client.relatedParties && this.client.relatedParties.length == 0) ||
      !this.client.relatedParties){
      this.client.relatedParties = [];
    }
    if ((this.client.thirdParties && this.client.thirdParties.length == 0) ||
      !this.client.thirdParties){
      this.client.thirdParties = [];
    }
  }

  appendIndyRelatedParty(){
    let indyClient = '';
    if (this.client.firstName){
      indyClient += this.client.firstName;
    }
    if (this.client.lastName){
      indyClient += ` ${this.client.lastName}`;
    }
    const defaultIndy = _.find(this.client.relatedParties, {default: true});
    if (defaultIndy){
      defaultIndy.name = indyClient;
    } else{
      this.client.relatedParties.push({
        name: indyClient,
        type: '1040',
        default: true
      });
    }
  }

  pushLabel(){
    if (!this.client.labels){
      this.client.labels = [];
    }
    if (this.tempLabelText){
      this.client.labels.push({
        label: this.tempLabelText
      });
      this.tempLabelText = '';
    }
  }

  removeLabel(index){
    this.client.labels.splice(index, 1);
  }

  setType($event, relatedPary){
    relatedPary.type = $event.name;
  }

  removeRelatedParty(index){
    this.client.relatedParties.splice(index, 1);
  }

  addRelatedParty(){
    this.client.relatedParties.push({});
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  closeCreateOrUpdatePopup() {
    this.onCancelCreateOrUpdateOfClient.emit();
  }

  saveClient(){
    if (!this.validateData()){
      return;
    }
    if (this.editMode){
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.updateClient(this.org, this.user.id, this.client.id, this.client).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.taxStrategyServices.clearFirmLabelsData();
        this.taxPlanningService.clearClientData();
        this.toastService.pop(TOAST_TYPE.success, 'Client updated successfully');
        this.onCreationOrUpdateOfClient.emit();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try{
          this.toastService.pop(TOAST_TYPE.error, error.message ? error.message : 'Failed to update client');
        } catch (e){
          this.toastService.pop(TOAST_TYPE.error, 'Failed to update client');
        }
      });
    } else {
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.createClient(this.org, this.user.id, this.client).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Client created successfully');
        this.taxStrategyServices.clearFirmLabelsData();
        this.onCreationOrUpdateOfClient.emit(response);
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try{
          const message = error.message ? error.message : 'Failed to create client';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e){
          this.toastService.pop(TOAST_TYPE.error, 'Failed to create client');
        }
      });
    }
  }

  validateData(){
    if (!this.client.firstName){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter First Name');
      return false;
    } else if (!this.client.lastName){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter Last Name');
      return false;
    }

    if (this.client.enableClientPortal) {
      if (
        !this.client.email ||
        !isValidEmail(this.client.email)
      ) {
        this.toastService.pop(TOAST_TYPE.error, 'Please enter a valid email');
        return false;
      }
    }
    return true;
  }

  setClientStatus($event){
    this.client.status = $event.name;
    this.client.customStatus = '';
  }

  getSubscriptionInfo() {
    this.taxPlanningService.getSubscriptionStatus(this.org, this.user.id).subscribe(subscription => {
      this.currentPlan = subscription.plan;
    });
  }

  scrollToBottom(): void {
    this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
  }
}
